import React, { useState, useEffect } from "react";
import {
  Button,
  Tag,
  Space,
  Drawer,
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  Progress,
  Badge,
  Alert,
  Form,
} from "antd";
import type { TableColumnsType } from "antd";
import {
  FilterOutlined,
  CalendarOutlined,
  DollarOutlined,
  UserOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  PhoneOutlined,
  ExclamationCircleOutlined,
  MailOutlined,
  SearchOutlined,
  ReloadOutlined,
  SaveOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";
import { Link, useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { Icon, TooltipComponent } from "../../components/Component";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import RABillAside from "./raBillAside";
import { formatCurrency } from "../../utils/Utils";
import { mockRABills } from "./mockRABills";
import { useSelector } from "react-redux";
import dropdownApis from "../../api/WorkOrderManagement/DropdownApis";

const { Option } = Select;
const { RangePicker } = DatePicker;

// Filter State Interface
interface FilterState {
  siteId?: string[];
  contractorName?: string[];
  workOrderId?: string[];
}

const RABillsOverdue: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get flowType from URL
  const searchParams = new URLSearchParams(location.search);
  const flowType = searchParams.get("flowType");
  const isClientFlow = flowType?.toLowerCase() === "client";
  
  const [sm, setSm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [filters, setFilters] = useState<FilterState>({});
  const [tempFilters, setTempFilters] = useState<FilterState>({});
  const [savedFilters, setSavedFilters] = useState<{
    [key: string]: FilterState;
  }>({});
  const [filterForm] = Form.useForm();
  const [contractorOptions, setContractorOptions] = useState<any[]>([]);
  const [siteOptions, setSiteOptions] = useState<any[]>([]);
  const [workOrderOptions, setWorkOrderOptions] = useState<any[]>([]);
  const { data: currentUser } = useSelector((state: any) => state.currentUser);

  const getSeverityColor = (severity: string): string => {
    const severityColors: { [key: string]: string } = {
      Critical: "#ff4d4f",
      High: "#fa8c16",
      Medium: "#faad14",
      Low: "#52c41a",
    };
    return severityColors[severity] || "#d9d9d9";
  };

  const getOverdueColor = (days: number): string => {
    if (days > 60) return "#ff4d4f";
    if (days > 30) return "#fa8c16";
    if (days > 0) return "#faad14";
    return "#52c41a";
  };

  const getPaymentProgress = (
    paidAmount: number,
    totalAmount: number
  ): number => {
    if (totalAmount === 0) return 0;
    return Math.round((paidAmount / totalAmount) * 100);
  };

  const handleViewRABill = (record: any) => {
    const searchParams = new URLSearchParams(location.search);
    const flowType = searchParams.get("flowType");
    const flowTypeQuery = flowType ? `&flowType=${flowType}` : "";
    navigate(
      `${process.env.PUBLIC_URL}/ra-bills-view-details/${record.raBillId}?mode=view${flowTypeQuery}`
    );
  };

  const handleEscalate = (record: any) => {
    // Implementation for escalation (send to management, etc.)
    console.log("Escalate:", record.raBillNumber);
  };

  const handleSendReminder = (record: any) => {
    // Implementation for sending payment reminder
    console.log("Send reminder for:", record.raBillNumber);
  };

  // Fetch site options from currentUser
  useEffect(() => {
    if (currentUser) {
      const { siteAccessibility } = currentUser;
      if (siteAccessibility) {
        const _siteOptions: any = [];
        siteAccessibility?.forEach((item: any) => {
          _siteOptions.push({ label: item.siteName, value: item.siteID });
        });
        setSiteOptions([..._siteOptions]);
      }
    }
  }, [currentUser]);

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value) && value.length > 0) count++;
        else if (typeof value === "string" && value.trim() !== "") count++;
      }
    });
    return count;
  };

  // Filter Functions
  const handleFilterDrawerOpen = async () => {
    setTempFilters({ ...filters });
    filterForm.setFieldsValue(filters);
    setFilterDrawerVisible(true);

    // Fetch dropdown data when drawer opens
    try {
      const flowTypeParam = isClientFlow ? "Client" : "Contractor";

      // Fetch contractors
      const partyType = isClientFlow ? "Client" : "Contractor";
      const contractorsResponse = await dropdownApis.fetchContractorDropDown(
        partyType
      );
      if (
        contractorsResponse &&
        contractorsResponse.status &&
        contractorsResponse.data
      ) {
        const contractorOptions = contractorsResponse.data.map((item: any) => ({
          label: item.companyName || "N/A",
          value: item.contractorID?.toString(),
        }));
        setContractorOptions(contractorOptions);
      }

      // Fetch work orders
      const workOrdersResponse = await dropdownApis.fetchWorkOrderDropdown(
        flowTypeParam
      );
      if (
        workOrdersResponse &&
        workOrdersResponse.status &&
        workOrdersResponse.data
      ) {
        const workOrderOptions = workOrdersResponse.data.map((item: any) => {
          const label =
            item.workOrderNumber ||
            item.workOrderSequenceId ||
            item.sequenceId ||
            item.workOrderTitle ||
            `WO-${item.workOrderId || item.id || "N/A"}`;
          const value = item.workOrderId?.toString() || item.id?.toString();
          return { label, value };
        });
        setWorkOrderOptions(workOrderOptions);
      }
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  const handleFilterDrawerClose = () => {
    setFilterDrawerVisible(false);
    setTempFilters({});
    filterForm.resetFields();
  };

  const handleApplyFilters = () => {
    filterForm
      .validateFields()
      .then((values) => {
        const processedFilters: FilterState = {
          siteId:
            values.siteId && values.siteId.length > 0
              ? values.siteId
              : undefined,
          contractorName:
            values.contractorName && values.contractorName.length > 0
              ? values.contractorName
              : undefined,
          workOrderId:
            values.workOrderId && values.workOrderId.length > 0
              ? values.workOrderId
              : undefined,
        };

        // Remove undefined values
        Object.keys(processedFilters).forEach((key) => {
          if (processedFilters[key as keyof FilterState] === undefined) {
            delete processedFilters[key as keyof FilterState];
          }
        });

        setFilters(processedFilters);
        setFilterDrawerVisible(false);
      })
      .catch((error) => {
        console.error("Form validation failed:", error);
      });
  };

  const handleResetFilters = () => {
    setFilters({});
    setTempFilters({});
    filterForm.resetFields();
  };

  const handleSaveFilter = () => {
    const filterName = prompt("Enter a name for this filter:");
    if (filterName && filterName.trim()) {
      setSavedFilters((prev) => ({
        ...prev,
        [filterName.trim()]: { ...filters },
      }));
    }
  };

  const handleLoadFilter = (filterName: string) => {
    const savedFilter = savedFilters[filterName];
    if (savedFilter) {
      setFilters(savedFilter);
      setTempFilters(savedFilter);
      filterForm.setFieldsValue(savedFilter);
    }
  };

  const columns: TableColumnsType<any> = [
    {
      title: "RA Bill #",
      dataIndex: "raBillSequenceId",
      key: "raBillSequenceId",
      width: 220,
      fixed: "left",
      render: (text: string, record: any) => (
        <div>
          <span
            className="inlineTableCellLink"
            id={`raBillOverdue-${record?.raBillId}`}
            onClick={() => handleViewRABill(record)}
          >
            {text}
          </span>
          {/* <div className="mt-1">
            <Tag color={getSeverityColor(record.severityLevel)}>
              {record?.severityLevel?.toUpperCase()}
            </Tag>
          </div> */}
        </div>
      ),
    },
    {
      title: "WO #",
      key: "workOrderNumber",
      dataIndex: "workOrderNumber",
      width: 220,
      // render: (_, record) => (
      //   <div>
      //     <div className="fw-bold text-primary">{record.workOrderNumber}</div>
      //     <div className="text-muted small mt-1">{record.contractor}</div>
      //   </div>
      // ),
    },
    {
      title: "Title",
      dataIndex: "workOrderTitle",
      key: "workOrderTitle",
      width: 250,
      render: (text: string, record: any, index: any) => {
        return (
          <>
            <span id={`title-tooltip-${index}`}>
              {text?.length > 30 ? `${text?.substring(0, 30)}... ` : text}
            </span>
            {text?.length > 30 && (
              <TooltipComponent
                iconClass="info-circle text-info"
                icon="info"
                id={`title-tooltip-${index}`}
                direction="top"
                text={text}
                containerClassName={""}
              />
            )}
          </>
        );
      },
    },
    {
      title: "Site/Project",
      key: "siteProject",
      width: 180,
      render: (_, record) => (
        <div>
          <div className="fw-bold">{record.siteName}</div>
          <div className="text-muted small">{record.projectName}</div>
        </div>
      ),
    },
    {
      title: "Outstanding Amount",
      dataIndex: "balanceAmount",
      key: "balanceAmount",
      width: 180,
      align: "right",
      render: (balance, record) => (
        <div>
          <div className="fw-bold text-danger">
            {formatCurrency(record.balanceAmount, "INR")}
          </div>
          <div className="text-muted small">
            of {formatCurrency(record.netPayable, "INR")}
          </div>
        </div>
      ),
    },
    {
      title: "Due Date",
      key: "paymentDueDate",
      width: 130,
      render: (_, record) => (
        <div>
          <div className="fw-bold text-danger">
            {dayjs(record.paymentDueDate).format("DD-MMM-YYYY")}
          </div>
        </div>
      ),
      sorter: (a, b) =>
        dayjs(a.expectedPaymentDate).unix() -
        dayjs(b.expectedPaymentDate).unix(),
    },
    {
      title: "Overdue By",
      key: "slaAgingForDue",
      width: 120,
      align: "center",
      render: (_, record) => (
        <div>
          <Badge
            count={`${record.slaAgingForDue}d`}
            color={getOverdueColor(record.slaAgingForDue)}
            style={{ fontSize: "12px" }}
          />
          <div className="text-muted small mt-1">
            {record.slaAgingForDue > 60
              ? "Critical"
              : record.slaAgingForDue > 30
              ? "High Risk"
              : "At Risk"}
          </div>
        </div>
      ),
      sorter: (a, b) => b.slaAgingForDue - a.slaAgingForDue,
    },
    {
      title: "Payment Progress",
      key: "progress",
      width: 140,
      align: "center",
      render: (_, record) => {
        const progress = getPaymentProgress(
          record.paidAmount,
          record.netPayable
        );
        return (
          <div>
            <Progress
              percent={progress}
              size="small"
              strokeColor={progress > 0 ? "#52c41a" : "#ff4d4f"}
              showInfo={false}
            />
            <div className="text-muted small mt-1">
              {formatCurrency(record.paidAmount, "INR")} paid
            </div>
          </div>
        );
      },
    },
    {
      title: "Last Contact",
      key: "lastContactDate",
      width: 130,
      render: (_, record) => (
        <div>
          {record.lastContactDate ? (
            <>
              <div className="fw-bold">
                {dayjs(record.lastContactDate).format("DD-MMM-YYYY")}
              </div>
              <div className="text-muted small">Payment</div>
            </>
          ) : (
            <>
              <div className="text-muted">No Contact</div>
              <div className="text-danger small">⚠️ Action Needed</div>
            </>
          )}
        </div>
      ),
    },
    {
      title: "Risk Level",
      key: "riskLevel",
      width: 120,
      render: (_, record) => {
        const riskLevel =
          record.slaAgingForDue > 90
            ? "Very High"
            : record.slaAgingForDue > 60
            ? "High"
            : record.slaAgingForDue > 30
            ? "Medium"
            : "Low";
        const color =
          record.slaAgingForDue > 90
            ? "#ff4d4f"
            : record.slaAgingForDue > 60
            ? "#fa8c16"
            : record.slaAgingForDue > 30
            ? "#faad14"
            : "#52c41a";

        return (
          <div className="text-center">
            <Tag color={color}>{riskLevel}</Tag>
            {record.slaAgingForDue > 60 && (
              <div className="text-danger small mt-1">
                <ExclamationCircleOutlined /> Escalate
              </div>
            )}
          </div>
        );
      },
    },
    // {
    //   title: "Actions",
    //   key: "actions",
    //   fixed: "right",
    //   width: 200,
    //   render: (_, record) => (
    //     <Space wrap size="small">
    //       <Button
    //         type="primary"
    //         size="small"
    //         icon={<EyeOutlined />}
    //         onClick={() => handleViewRABill(record)}
    //       >
    //         View
    //       </Button>
    //     </Space>
    //   ),
    // },
  ];

  return (
    <>
      {/* Critical Alert Banner */}
      {/* {criticalCount > 0 && (
        <Alert
          type="error"
          showIcon
          message={`Critical Alert: ${criticalCount} bills are critically overdue (60+ days)`}
          description="Immediate action required for payment collection and risk mitigation"
          style={{ marginBottom: 16 }}
          closable
        />
      )} */}

      <TransactionFlowDefaultTemplate
        moduleKey="ra-bills-overdue"
        title="Overdue RA Bills"
        subtitle="Track and escalate overdue Running Account Bills requiring immediate attention"
        columns={columns}
        loading={loading}
        customFilters={filters}
        sideNavigation={
          <div
            className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
              sm ? "content-active" : ""
            }`}
          >
            <RABillAside updateSm={setSm} sm={sm} />
          </div>
        }
        topRightJSX={
          <Space>
            <Badge count={getActiveFilterCount()} size="small">
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={handleFilterDrawerOpen}
              >
                Advanced Filters
              </Button>
            </Badge>
          </Space>
        }
        extra={{
          tableScrollX: 1900,
          showSearch: true,
          searchPlaceholder: "Search by RA Bill#, WO#, or Contractor...",
        }}
      />

      {/* Advanced Filters Drawer */}
      <Drawer
        title="Advanced Filters"
        width={420}
        open={filterDrawerVisible}
        onClose={handleFilterDrawerClose}
        extra={
          <Space size="middle">
            <Badge count={getActiveFilterCount()} size="small">
              <Button size="small" type="text" icon={<FilterOutlined />} />
            </Badge>
            <Button
              size="small"
              onClick={handleResetFilters}
              icon={<ReloadOutlined />}
            >
              Reset
            </Button>
          </Space>
        }
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button
                size="small"
                onClick={handleSaveFilter}
                icon={<SaveOutlined />}
              >
                Save Filter
              </Button>
              {Object.keys(savedFilters).length > 0 && (
                <Select
                  placeholder="Load saved filter"
                  size="small"
                  style={{ width: 150 }}
                  suffixIcon={<FolderOpenOutlined />}
                  onChange={handleLoadFilter}
                  allowClear
                >
                  {Object.keys(savedFilters).map((filterName) => (
                    <Option key={filterName} value={filterName}>
                      {filterName}
                    </Option>
                  ))}
                </Select>
              )}
            </Space>
            <Space>
              <Button onClick={handleFilterDrawerClose}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleApplyFilters}
                icon={<SearchOutlined />}
              >
                Apply Filters
              </Button>
            </Space>
          </div>
        }
      >
        <Form form={filterForm} layout="vertical" initialValues={filters}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item name="siteId" label="Site/Project">
                <Select
                  mode="multiple"
                  placeholder="Select Sites"
                  options={siteOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="contractorName"
                label={isClientFlow ? "Client" : "Contractor"}
              >
                <Select
                  mode="multiple"
                  placeholder={
                    isClientFlow ? "Select Clients" : "Select Contractors"
                  }
                  options={contractorOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="workOrderId" label="Work Order">
                <Select
                  mode="multiple"
                  placeholder="Select Work Orders"
                  options={workOrderOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>
    </>
  );
};

export default RABillsOverdue;
